import React from 'react';
import Icon from '../../../components/AppIcon';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })?.format(amount || 0);

const CustomerDetailDrawer = ({ customer, onClose, onCreateBid, onCreateProject }) => {
  if (!customer) return null;

  const companyName = customer?.companyName || customer?.company_name || '';
  const contactPerson = customer?.contactPerson || customer?.contact_person || '';
  const email = customer?.email || '';
  const phone = customer?.phone || '';
  const address = customer?.address || '';
  const city = customer?.city || '';
  const state = customer?.state || '';
  const activeProjects = customer?.activeProjects ?? customer?.active_projects ?? 0;
  const totalValue = customer?.totalValue ?? customer?.total_value ?? 0;
  const notes = customer?.notes || '';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-40 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Drawer */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-card rounded-t-3xl shadow-2xl max-h-[85vh] flex flex-col">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 bg-muted-foreground/30 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
              <Icon name="Building2" size={20} className="text-accent" />
            </div>
            <div>
              <h2 className="text-base font-bold text-foreground capitalize">{companyName}</h2>
              <p className="text-xs text-muted-foreground capitalize">{contactPerson}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-muted flex items-center justify-center"
          >
            <Icon name="X" size={16} className="text-muted-foreground" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          {/* Contact info */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Contact Information</h3>
            {email && (
              <a href={`mailto:${email}`} className="flex items-center gap-3 min-h-[44px]">
                <div className="w-9 h-9 rounded-xl bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="Mail" size={16} className="text-blue-500" />
                </div>
                <span className="text-sm text-foreground">{email}</span>
              </a>
            )}
            {phone && (
              <a href={`tel:${phone}`} className="flex items-center gap-3 min-h-[44px]">
                <div className="w-9 h-9 rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="Phone" size={16} className="text-emerald-500" />
                </div>
                <span className="text-sm text-foreground">{phone}</span>
              </a>
            )}
            {(address || city) && (
              <div className="flex items-center gap-3 min-h-[44px]">
                <div className="w-9 h-9 rounded-xl bg-violet-500/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="MapPin" size={16} className="text-violet-500" />
                </div>
                <span className="text-sm text-foreground">
                  {[address, city, state]?.filter(Boolean)?.join(', ')}
                </span>
              </div>
            )}
          </div>

          {/* Financial summary */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-muted/50 rounded-2xl p-3">
              <p className="text-xs text-muted-foreground mb-1">Active Projects</p>
              <p className="text-xl font-bold text-foreground">{activeProjects}</p>
            </div>
            <div className="bg-muted/50 rounded-2xl p-3">
              <p className="text-xs text-muted-foreground mb-1">Total Value</p>
              <p className="text-lg font-bold text-emerald-500">{formatCurrency(totalValue)}</p>
            </div>
          </div>

          {/* Notes */}
          {notes && (
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Notes</h3>
              <p className="text-sm text-foreground bg-muted/40 rounded-xl p-3">{notes}</p>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="px-5 py-4 border-t border-border flex gap-3 flex-shrink-0">
          <button
            onClick={() => { onCreateBid(customer); onClose(); }}
            className="flex-1 flex items-center justify-center gap-2 bg-amber-500 text-white rounded-2xl py-3.5 text-sm font-semibold min-h-[44px] active:bg-amber-600 transition-colors"
          >
            <Icon name="FileText" size={16} />
            Create Bid
          </button>
          <button
            onClick={() => { onCreateProject(customer); onClose(); }}
            className="flex-1 flex items-center justify-center gap-2 bg-accent text-white rounded-2xl py-3.5 text-sm font-semibold min-h-[44px] active:opacity-90 transition-colors"
          >
            <Icon name="Briefcase" size={16} />
            Create Project
          </button>
        </div>
      </div>
    </>
  );
};

export default CustomerDetailDrawer;
